import type { TeactNode } from '../../lib/teact/teact';
import React, { memo, useCallback, useEffect, useMemo, useState } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { ApiBaseCurrency, ApiStakingState } from '../../api/types';
import type { GlobalState, Theme, UserToken } from '../../global/types';
import { ApiTransactionDraftError } from '../../api/types';
import { StakingState } from '../../global/types';

import {
  ANIMATED_STICKER_TINY_ICON_PX,
  DEFAULT_PRICE_CURRENCY,
  TONCOIN,
  VALIDATION_PERIOD_MS,
} from '../../config';
import renderText from '../../global/helpers/renderText';
import {
  selectAccountStakingState,
  selectCurrentAccount,
  selectCurrentAccountId,
  selectCurrentAccountTokens,
  selectHasMultipleAccounts,
  selectIsCurrentAccountViewMode,
} from '../../global/selectors';
import { getDoesUsePinPad } from '../../util/biometrics';
import buildClassName from '../../util/buildClassName';
import { formatRelativeHumanDateTime } from '../../util/dateFormat';
import { toDecimal } from '../../util/decimals';
import { getTonStakingFees } from '../../util/fee/getTonOperationFees';
import { formatCurrency } from '../../util/formatNumber';
import { getIsViewAccountDisabled } from '../../util/isViewAccount';
import resolveSlideTransitionName from '../../util/resolveSlideTransitionName';
import { getIsLongUnstake, getUnstakeTime } from '../../util/staking';
import { getIsMobileTelegramApp } from '../../util/windowEnvironment';
import { ANIMATED_STICKERS_PATHS } from '../ui/helpers/animatedAssets';

import useAppTheme from '../../hooks/useAppTheme';
import useForceUpdate from '../../hooks/useForceUpdate';
import useInterval from '../../hooks/useInterval';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import useModalTransitionKeys from '../../hooks/useModalTransitionKeys';
import useSyncEffect from '../../hooks/useSyncEffect';
import { useAmountInputState } from '../ui/hooks/useAmountInputState';

import AccountSwitcherPill from '../common/AccountSwitcherPill';
import AccountSwitcherSlide from '../common/AccountSwitcherSlide';
import MfaConfirm from '../common/MfaConfirm';
import TransactionBanner from '../common/TransactionBanner';
import TransferResult from '../common/TransferResult';
import LedgerConfirmOperation from '../ledger/LedgerConfirmOperation';
import LedgerConnect from '../ledger/LedgerConnect';
import AmountInput from '../ui/AmountInput';
import AnimatedIconWithPreview from '../ui/AnimatedIconWithPreview';
import Button from '../ui/Button';
import Fee from '../ui/Fee';
import Modal from '../ui/Modal';
import ModalHeader from '../ui/ModalHeader';
import PasswordForm from '../ui/PasswordForm';
import Transition from '../ui/Transition';

import modalStyles from '../ui/Modal.module.scss';
import styles from './Staking.module.scss';

type StateProps = GlobalState['currentStaking'] & {
  isViewMode: boolean;
  tokens?: UserToken[];
  baseCurrency: ApiBaseCurrency;
  isNominators?: boolean;
  theme: Theme;
  stakingState?: ApiStakingState;
  isSensitiveDataHidden?: true;
  accountId?: string;
  accountTitle?: string;
  hasMultipleAccounts?: boolean;
};

const IS_OPEN_STATES = new Set([
  StakingState.UnstakeInitial,
  StakingState.UnstakePassword,
  StakingState.UnstakeConnectHardware,
  StakingState.UnstakeConfirmHardware,
  StakingState.UnstakeConfirmMfa,
  StakingState.UnstakeComplete,
  StakingState.UnstakeSelectAccount,
]);

const UPDATE_UNSTAKE_DATE_INTERVAL_MS = 30000; // 30 sec

function UnstakeModal({
  state,
  isViewMode,
  isLoading,
  error,
  tokens,
  baseCurrency,
  isNominators,
  theme,
  amount,
  mfaRequestHash,
  stakingState,
  isSensitiveDataHidden,
  accountId,
  accountTitle,
  hasMultipleAccounts,
}: StateProps) {
  const {
    setStakingScreen,
    cancelStaking,
    clearStakingError,
    submitStakingInitial,
    submitStaking,
    fetchStakingHistory,
    updateStakingMfaRequestStatus,
    switchStakingAccount,
  } = getActions();

  const {
    type: stakingType,
    tokenSlug,
    balance: stakingBalance,
  } = stakingState ?? {};

  const lang = useLang();
  const isOpen = IS_OPEN_STATES.has(state);

  const { gas: networkFee, real: realFee } = getTonStakingFees(stakingState?.type).unstake;

  const nativeToken = useMemo(() => tokens?.find(({ slug }) => slug === TONCOIN.slug), [tokens]);
  const isNativeEnough = nativeToken && nativeToken.amount >= networkFee;
  const instantAvailable = stakingState?.type === 'liquid' ? stakingState.instantAvailable : undefined;

  const token = useMemo(() => {
    return tokenSlug ? tokens?.find(({ slug }) => slug === tokenSlug) : undefined;
  }, [tokenSlug, tokens]);

  const isOnlyFullAmount = isNominators;
  const [unstakeAmount, setUnstakeAmount] = useState(isOnlyFullAmount ? stakingBalance : undefined);
  const [successUnstakeAmount, setSuccessUnstakeAmount] = useState<bigint | undefined>(undefined);

  const unstakeTime = getUnstakeTime(stakingState);
  const isLongUnstake = stakingState ? getIsLongUnstake(stakingState, unstakeAmount) : undefined;

  // A staking state without an unstake request has `end` of 0, which must not look like an expired unstake
  const [unstakeDate, setUnstakeDate] = useState<number>(unstakeTime || Date.now() + VALIDATION_PERIOD_MS);
  const forceUpdate = useForceUpdate();
  const appTheme = useAppTheme(theme);

  const { renderingKey, nextKey, updateNextKey } = useModalTransitionKeys(state, isOpen);

  const isAmountGreaterThanBalance = unstakeAmount !== undefined && (!stakingBalance || unstakeAmount > stakingBalance);
  const hasAmountError = isAmountGreaterThanBalance || (unstakeAmount !== undefined && unstakeAmount < 0);

  const isInsufficientBalance = error === ApiTransactionDraftError.InsufficientBalance || isAmountGreaterThanBalance;
  // `isNativeEnough` is `undefined` until the tokens load, which must not look like a fee shortage
  const isInsufficientFee = isNativeEnough === false;

  const isUnstakeDisabled = !isNativeEnough || isInsufficientBalance || !unstakeAmount;

  useEffect(() => {
    if (isOpen) {
      fetchStakingHistory();
      setUnstakeAmount(isOnlyFullAmount ? stakingBalance : undefined);
    }
  }, [isOpen, fetchStakingHistory, isOnlyFullAmount, stakingBalance]);

  useSyncEffect(() => {
    if (unstakeTime) {
      setUnstakeDate(unstakeTime);
    }
  }, [unstakeTime]);

  const refreshUnstakeDate = useLastCallback(() => {
    if (unstakeDate < Date.now()) {
      fetchStakingHistory();
    }
    forceUpdate();
  });

  // The countdown is visible only in the open modal. The modal stays mounted while closed, and in the explorer it
  // mounts before any account exists. Opening the modal already loads the history, so the first tick is skipped.
  useInterval(refreshUnstakeDate, isOpen ? UPDATE_UNSTAKE_DATE_INTERVAL_MS : undefined, true);
  useInterval(() => {
    if (state === StakingState.UnstakeConfirmMfa && mfaRequestHash) {
      updateStakingMfaRequestStatus();
    }
  }, state === StakingState.UnstakeConfirmMfa ? 1000 : undefined);

  const handleBackClick = useLastCallback(() => {
    if (state === StakingState.UnstakePassword) {
      setStakingScreen({ state: StakingState.UnstakeInitial });
    }
  });

  const handleOpenAccountSelector = useLastCallback(() => {
    setStakingScreen({ state: StakingState.UnstakeSelectAccount });
  });

  const handleSelectAccount = useLastCallback((nextAccountId: string) => {
    switchStakingAccount({ accountId: nextAccountId, mode: 'unstake' });
  });

  const handleSelectAccountBack = useLastCallback(() => {
    setStakingScreen({ state: StakingState.UnstakeInitial });
  });

  const handleStartUnstakeClick = useLastCallback(() => {
    submitStakingInitial({ isUnstaking: true, amount: unstakeAmount });
  });

  const handleAuthorize = useLastCallback((enclaveToken: string) => {
    setSuccessUnstakeAmount(amount);

    submitStaking({ enclaveToken, isUnstaking: true });
  });

  const handleLedgerConnect = useLastCallback(() => {
    setSuccessUnstakeAmount(amount);
    submitStaking({ isUnstaking: true });
  });

  function renderTransactionBanner() {
    if (!token || !unstakeAmount) return undefined;

    return (
      <TransactionBanner
        tokenIn={token}
        withChainIcon
        color="green"
        text={formatCurrency(toDecimal(unstakeAmount, token.decimals), token.symbol)}
        className={!getDoesUsePinPad() ? styles.transactionBanner : undefined}
      />
    );
  }

  const amountInputProps = useAmountInputState({
    amount: unstakeAmount,
    token,
    baseCurrency,
    isAmountReadonly: isOnlyFullAmount,
    onAmountChange: setUnstakeAmount,
  });

  // Wa want to render just the token name, not a token select button. To achieve that, we rely on the fact that
  // `Dropdown` renders with no button border when there is only 1 item.
  const selectableTokens = useMemo(() => token && [token], [token]);

  // It is necessary to use useCallback instead of useLastCallback here
  const renderBottomRight = useCallback((className?: string) => {
    const activeKey = isInsufficientFee ? 0
      : instantAvailable ? 1
        : 2;

    let content: string | React.JSX.Element | TeactNode[] = '';

    if (token) {
      if (isInsufficientFee) {
        // The real fee is smaller than the amount the request must be sent with, so a shortage shows the latter
        content = lang('$fee_value', {
          fee: <Fee terms={{ native: networkFee }} precision="exact" token={token} />,
        });
      } else if (instantAvailable) {
        content = lang('$unstake_up_to_information', {
          value: formatCurrency(toDecimal(instantAvailable, token.decimals), token.symbol),
        });
      } else {
        content = lang('$fee_value', {
          fee: <Fee terms={{ native: realFee }} precision="approximate" token={token} />,
        });
      }
    }

    return (
      <Transition
        className={buildClassName(
          styles.amountBottomRight,
          isInsufficientFee && styles.amountBottomRight_error,
          className,
        )}
        slideClassName={styles.amountBottomRight_slide}
        name="semiFade"
        activeKey={activeKey}
      >
        {content}
      </Transition>
    );
  }, [instantAvailable, isInsufficientFee, lang, networkFee, realFee, token]);

  function renderUnstakeTimer() {
    const unstakeDateText = stakingType === 'ethena'
      ? renderText(lang('$unstaking_when_receive_ethena'))
      : lang('$unstaking_when_receive', {
        time: (
          <strong>{formatRelativeHumanDateTime(lang.code, unstakeDate)}</strong>
        ),
      });

    return (
      <div className={buildClassName(styles.unstakeTime)}>
        <AnimatedIconWithPreview
          play={isOpen}
          size={ANIMATED_STICKER_TINY_ICON_PX}
          className={styles.unstakeTimeIcon}
          nonInteractive
          noLoop={false}
          tgsUrl={ANIMATED_STICKERS_PATHS[appTheme].iconClockGray}
          previewUrl={ANIMATED_STICKERS_PATHS[appTheme].preview.iconClockGray}
        />
        <div>{unstakeDateText}</div>
      </div>
    );
  }

  function renderUnstakeInfo() {
    const shouldShowInfo = !isInsufficientBalance && unstakeAmount;

    return (
      <Transition
        name="fade"
        activeKey={!shouldShowInfo ? 0 : !isLongUnstake ? 1 : 2}
        className={styles.unstakeInfoTransition}
      >
        {!shouldShowInfo ? undefined
          : (
            !isLongUnstake
              ? (
                <div className={styles.unstakeInfo}>
                  {lang('$unstake_information_instantly')}
                </div>
              )
              : renderUnstakeTimer()
          )}
      </Transition>
    );
  }

  function renderInitial() {
    return (
      <>
        <div
          className={buildClassName(styles.initialHeader, hasMultipleAccounts && styles.initialHeaderWithSwitcher)}
        >
          <ModalHeader title={lang('$unstake_asset', { symbol: token?.symbol })} onClose={cancelStaking} />
          {hasMultipleAccounts && accountId && (
            <AccountSwitcherPill
              accountId={accountId}
              title={accountTitle}
              className={styles.accountPill}
              onClick={handleOpenAccountSelector}
            />
          )}
        </div>
        <div className={modalStyles.transitionContent}>
          <AmountInput
            {...amountInputProps}
            labelText={lang('Amount to unstake')}
            maxAmount={stakingBalance}
            token={token}
            allTokens={selectableTokens}
            hasError={hasAmountError}
            hasMaxAmountError={isAmountGreaterThanBalance}
            isMaxAmountLoading={stakingBalance === undefined || !token}
            isMaxAmountAllMode
            isSensitiveDataHidden={isSensitiveDataHidden}
            renderBottomRight={renderBottomRight}
          />

          {renderUnstakeInfo()}

          {!isViewMode && (
            <div className={modalStyles.buttons}>
              <Button
                isPrimary
                isLoading={isLoading}
                isDisabled={isUnstakeDisabled}
                onClick={handleStartUnstakeClick}
              >
                {isInsufficientBalance
                  ? lang('Insufficient Balance')
                  : isInsufficientFee
                    ? lang('Insufficient Fee')
                    : lang('Unstake')}
              </Button>
            </div>
          )}
        </div>
      </>
    );
  }

  function renderPassword(isActive: boolean) {
    const placeholder = getDoesUsePinPad()
      ? 'Confirm action with your passcode'
      : 'Confirm action with your password';

    return (
      <>
        {!getDoesUsePinPad() && (
          <ModalHeader title={lang('Confirm Unstaking')} onClose={cancelStaking} />
        )}
        <PasswordForm
          isActive={isActive}
          isLoading={isLoading}
          operationType="unstaking"
          error={error}
          placeholder={lang(placeholder)}
          submitLabel={lang('Confirm')}
          cancelLabel={lang('Back')}
          onAuthorize={handleAuthorize}
          onCancel={handleBackClick}
          onUpdate={clearStakingError}
        >
          {renderTransactionBanner()}
        </PasswordForm>
      </>
    );
  }

  function renderComplete(isActive: boolean) {
    const title = getIsMobileTelegramApp() || isLongUnstake
      ? lang('Unstake Requested')
      : lang('Unstaked');

    return (
      <>
        <ModalHeader
          title={title}
          onClose={cancelStaking}
        />

        <div className={modalStyles.transitionContent}>
          <TransferResult
            color={!isLongUnstake ? 'green' : undefined}
            playAnimation={isActive}
            amount={successUnstakeAmount}
            tokenSymbol={token?.symbol}
            decimals={token?.decimals}
            noSign
          />

          {isLongUnstake && renderUnstakeTimer()}

          <div className={modalStyles.buttons}>
            <Button onClick={cancelStaking} isPrimary>{lang('Close')}</Button>
          </div>
        </div>
      </>
    );
  }

  function renderContent(isActive: boolean, isFrom: boolean, currentKey: StakingState) {
    switch (currentKey) {
      case StakingState.UnstakeInitial:
        return renderInitial();

      case StakingState.UnstakePassword:
        return renderPassword(isActive);

      case StakingState.UnstakeConnectHardware:
        return (
          <LedgerConnect
            isActive={isActive}
            onConnected={handleLedgerConnect}
            onClose={cancelStaking}
          />
        );

      case StakingState.UnstakeConfirmHardware:
        return (
          <LedgerConfirmOperation
            text={lang('Please confirm action on your Ledger')}
            error={error}
            onClose={cancelStaking}
            onTryAgain={handleLedgerConnect}
          />
        );

      case StakingState.UnstakeConfirmMfa:
        return (
          <>
            <ModalHeader onClose={cancelStaking} />
            <MfaConfirm
              onClose={cancelStaking}
              mfaRequestHash={mfaRequestHash}
            />
          </>
        );

      case StakingState.UnstakeComplete:
        return renderComplete(isActive);

      case StakingState.UnstakeSelectAccount:
        return (
          <AccountSwitcherSlide
            isActive={isActive}
            getIsAccountDisabled={getIsViewAccountDisabled}
            onAccountSelect={handleSelectAccount}
            onBack={handleSelectAccountBack}
            onClose={cancelStaking}
          />
        );
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      hasCloseButton
      noBackdropClose
      dialogClassName={styles.modalDialog}
      onClose={cancelStaking}
      onCloseAnimationEnd={updateNextKey}
    >
      <Transition
        name={resolveSlideTransitionName()}
        className={buildClassName(modalStyles.transition, modalStyles.transition_stableScroll, 'custom-scroll')}
        slideClassName={modalStyles.transitionSlide}
        activeKey={renderingKey}
        nextKey={nextKey}
        onStop={updateNextKey}
      >
        {renderContent}
      </Transition>
    </Modal>
  );
}

export default memo(withGlobal((global): StateProps => {
  const accountId = selectCurrentAccountId(global)!;
  const tokens = selectCurrentAccountTokens(global);
  const { baseCurrency = DEFAULT_PRICE_CURRENCY, isSensitiveDataHidden } = global.settings;
  const stakingState = selectAccountStakingState(global, accountId);
  const isNominators = stakingState?.type === 'nominators';

  return {
    ...global.currentStaking,
    tokens,
    baseCurrency,
    isNominators,
    theme: global.settings.theme,
    stakingState,
    isSensitiveDataHidden,
    isViewMode: selectIsCurrentAccountViewMode(global),
    accountId,
    accountTitle: selectCurrentAccount(global)?.title,
    hasMultipleAccounts: selectHasMultipleAccounts(global),
  };
})(UnstakeModal));
